/**
 * The first-sign-in tour of the till.
 *
 * Short on purpose — a salesperson learns the till by selling — so it points at where things are
 * and stops: the shift, search, the rare "other item", the cart and Charge once there is a cart,
 * and the tabs at the bottom. Steps whose button is not on screen (no cart yet, a shift already
 * open, a tab the person cannot use) are left out.
 *
 * Starts by itself the first time a person reaches the sale screen, and again from Profile.
 * Next is the primary action, Back an outline, "Skip tour" a quiet link. Below 768px the card is
 * a bottom sheet, as every sheet on the till is.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';

import { selectUser, tourSeen, useMarkTourSeenMutation, useTour, type TourStep } from '@pos/shared';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { TWO_PANEL, useMediaQuery } from '@/shared/lib/useMediaQuery';
import { Button, cn } from '@/shared/ui';

import { START_POS_TOUR_EVENT } from '../lib/startTour.js';


const STEPS: readonly TourStep[] = [
  {
    target: 'shift-banner',
    title: 'Open your shift first',
    body: 'Tap here at the start of the day. Everything you take is then counted as yours until you hand it over.',
  },
  {
    target: 'search',
    title: 'Find what you are selling',
    body: 'Type a name, scan a barcode, or type the last 4 digits of a phone’s IMEI. Tap it to add it to the sale, then tap Charge.',
  },
  {
    target: 'other-item',
    title: 'A repair or something not listed',
    body: 'For a service, or an item that is not in the product list. Say what it is and the price.',
  },
  {
    target: 'price-type',
    title: 'Retail or wholesale',
    body: 'Switch the whole sale to wholesale when the customer buys enough pieces. Each line says if it qualifies.',
  },
  {
    target: 'charge',
    title: 'Charge',
    body: 'Takes the customer to paying: cash, cash with change, or M-Pesa. The receipt comes next.',
  },
  {
    target: 'tab-sales',
    title: 'My sales',
    body: 'Everything you sold today. Open one to print the receipt or ask a manager to reverse it.',
  },
  {
    target: 'tab-shift',
    title: 'My cash',
    body: 'Open your shift, see what you should be holding, and hand the cash over at the end of the day.',
  },
  {
    target: 'tab-profile',
    title: 'Profile',
    body: 'Set a PIN so you do not type your password all day, choose light or dark, and take this tour again.',
  },
];

/** The sale screen answers at both: `/` is where signing in lands. */
const SALE_SCREEN = new Set(['/', '/sell']);

const CARD_WIDTH = 340;
const GAP = 12;
const PAD = 6;

export function PosTour() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const { pathname } = useLocation();
  const [markSeen] = useMarkTourSeenMutation();
  const wide = useMediaQuery(TWO_PANEL);

  const [open, setOpen] = useState(false);
  const autoStarted = useRef(false);

  // Only on the sale screen — that is what it is a tour of. `false`, not absent: an older server
  // that sends no flag must not show it to everyone.
  useEffect(() => {
    if (autoStarted.current || user?.posTourSeen !== false || !SALE_SCREEN.has(pathname)) return;
    // Marked when it fires: in development React runs this effect twice, and a marker set on the
    // first run would stop the second from ever starting it.
    const timer = window.setTimeout(() => {
      autoStarted.current = true;
      setOpen(true);
    }, 600);
    return () => {
      window.clearTimeout(timer);
    };
  }, [user?.posTourSeen, pathname]);

  useEffect(() => {
    const start = () => {
      setOpen(true);
    };
    window.addEventListener(START_POS_TOUR_EVENT, start);
    return () => {
      window.removeEventListener(START_POS_TOUR_EVENT, start);
    };
  }, []);

  const returnFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) returnFocus.current = document.activeElement as HTMLElement | null;
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    if (user?.posTourSeen === false) {
      dispatch(tourSeen('pos'));
      void markSeen('pos');
    }
    returnFocus.current?.focus();
  }, [dispatch, markSeen, user?.posTourSeen]);

  const tour = useTour({ steps: STEPS, open, onClose: close });

  if (!tour.open || tour.step === null) return null;

  return <TourCard tour={tour} sheet={!wide} />;
}

function TourCard({ tour, sheet }: { tour: ReturnType<typeof useTour>; sheet: boolean }) {
  const card = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(180);
  const step = tour.step!;
  const rect = tour.rect;
  const last = tour.index === tour.count - 1;
  const located = rect !== null;

  useLayoutEffect(() => {
    if (card.current) setHeight(card.current.offsetHeight);
    card.current?.focus();
  }, [tour.index, located]);

  const clampLeft = (left: number) => Math.min(Math.max(GAP, left), window.innerWidth - CARD_WIDTH - GAP);
  const clampTop = (top: number) => Math.min(Math.max(GAP, top), window.innerHeight - height - GAP);

  let position = { top: window.innerHeight / 2 - height / 2, left: window.innerWidth / 2 - CARD_WIDTH / 2 };
  if (rect !== null) {
    const below = rect.top + rect.height + GAP + PAD;
    position =
      below + height < window.innerHeight
        ? { top: below, left: clampLeft(rect.left) }
        : { top: clampTop(rect.top - GAP - PAD - height), left: clampLeft(rect.left) };
  }

  const titleId = `till-tour-title-${tour.index}`;

  return createPortal(
    <>
      {/* The page underneath is for looking at until the tour is closed. */}
      <div className="fixed inset-0 z-[60]" aria-hidden />

      {rect !== null && (
        <div
          aria-hidden
          className="pointer-events-none fixed z-[61] rounded-md outline outline-2 outline-action transition-all duration-base"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: '0 0 0 9999px var(--pos-scrim)',
          }}
        />
      )}

      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'fixed z-[62] bg-surface-raised p-5 text-content-primary shadow-xl outline-none',
          sheet
            ? 'inset-x-0 bottom-0 rounded-t-sheet pb-[max(1.25rem,env(safe-area-inset-bottom))]'
            : 'rounded-card',
        )}
        style={sheet ? undefined : { top: position.top, left: position.left, width: CARD_WIDTH }}
      >
        <p className="text-xs font-semibold text-content-muted">
          {tour.index + 1} of {tour.count}
        </p>
        <p id={titleId} className="mt-1 text-base font-bold">
          {step.title}
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-content-secondary">{step.body}</p>

        <div className={cn('mt-4 flex gap-2', sheet ? 'flex-col-reverse' : 'items-center')}>
          <Button variant="ghost" size="md" onClick={tour.close} className={cn(!sheet && 'mr-auto')}>
            Skip tour
          </Button>
          {tour.index > 0 && (
            <Button variant="outline" size="md" fullWidth={sheet} onClick={tour.previous}>
              Back
            </Button>
          )}
          <Button size="md" fullWidth={sheet} onClick={tour.next}>
            {last ? 'Done' : 'Next'}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}
